import FWU_TARGET_TYPES from '../../enums/iot/fwuTargetTypes';
import IOT_DEVICE_TARGET_NAMES from '../../enums/iot/iodDeviceTargetNames';
import {IDevice, IDeviceEngine, IHolder, IHolderEngine, IIotDevice, IIotDeviceTargets} from '../../models/iotDevice';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {getState} from '../../state/store';

const isFwuTargetDevice = (fwuTarget: FWU_TARGET_TYPES): boolean => fwuTarget === FWU_TARGET_TYPES.CHARGER_OR_MONO;
const isFwuTargetHolderEngine = (fwuTarget: FWU_TARGET_TYPES): boolean => fwuTarget === FWU_TARGET_TYPES.HOLDER_ENGINE;
const isFwuTargetBleStack = (fwuTarget: FWU_TARGET_TYPES): boolean => fwuTarget === FWU_TARGET_TYPES.BLE_STACK;

const isFwuTargetRequiresUsbConnection = (fwuTarget: FWU_TARGET_TYPES, doesDeviceSupportSblUpdate: boolean): boolean =>
    fwuTarget === FWU_TARGET_TYPES.BLE_STACK ||
    (fwuTarget === FWU_TARGET_TYPES.HOLDER_DEVICE && !doesDeviceSupportSblUpdate) ||
    (fwuTarget === FWU_TARGET_TYPES.HOLDER_ENGINE && !doesDeviceSupportSblUpdate) ||
    (isFwuTargetRequiresSblTypeUpdate(fwuTarget) && !doesDeviceSupportSblUpdate);

const isFwuTargetRequiresSblTypeUpdate = (fwuTarget: FWU_TARGET_TYPES): boolean =>
    fwuTarget === FWU_TARGET_TYPES.HOLDER_DEVICE ||
    fwuTarget === FWU_TARGET_TYPES.HOLDER_ENGINE ||
    fwuTarget === FWU_TARGET_TYPES.MONO_ENGINE;

const isFwuTargetHolderRelated = (fwuTarget?: FWU_TARGET_TYPES): boolean =>
    fwuTarget === FWU_TARGET_TYPES.HOLDER_DEVICE || fwuTarget === FWU_TARGET_TYPES.HOLDER_ENGINE;

const getIotTargetByFwuTarget = (
    iotDevice: IIotDevice,
    fwu_target: FWU_TARGET_TYPES
): IDevice | IDeviceEngine | IHolder | IHolderEngine | undefined => {
    const iotTargetName = getIotTargetNameByFwuTarget(fwu_target);

    return iotDevice ? iotDevice[iotTargetName] : undefined;
};

const getFwuTargetByIotTarget = (iotDevice: IIotDevice): FWU_TARGET_TYPES | undefined => {
    const {device, device_engine, holder, holder_engine, ble_stack} = iotDevice || {};

    if (device) {
        return FWU_TARGET_TYPES.CHARGER_OR_MONO;
    } else if (device_engine) {
        return FWU_TARGET_TYPES.MONO_ENGINE;
    } else if (ble_stack) {
        return FWU_TARGET_TYPES.BLE_STACK;
    } else if (holder) {
        return FWU_TARGET_TYPES.HOLDER_DEVICE;
    } else if (holder_engine) {
        return FWU_TARGET_TYPES.HOLDER_ENGINE;
    }
};

const getIotTargetNameByFwuTarget = (fwu_target: FWU_TARGET_TYPES): IOT_DEVICE_TARGET_NAMES => {
    switch (fwu_target) {
        case FWU_TARGET_TYPES.MONO_ENGINE:
            return IOT_DEVICE_TARGET_NAMES.DEVICE_ENGINE;
        case FWU_TARGET_TYPES.BLE_STACK:
            return IOT_DEVICE_TARGET_NAMES.BLE_STACK;
        case FWU_TARGET_TYPES.HOLDER_DEVICE:
            return IOT_DEVICE_TARGET_NAMES.HOLDER;
        case FWU_TARGET_TYPES.HOLDER_ENGINE:
            return IOT_DEVICE_TARGET_NAMES.HOLDER_ENGINE;
        case FWU_TARGET_TYPES.CHARGER_OR_MONO:
        default:
            return IOT_DEVICE_TARGET_NAMES.DEVICE;
    }
};

const getFwuTargets = (isHolder?: boolean): IIotDeviceTargets => {
    const iotDevice = makeSelectIotDeviceData()(getState() as never);
    const {device, holder} = iotDevice || {};

    if (isHolder === undefined) {
        return {device, holder};
    }
    if (isHolder) {
        return {holder};
    }

    return {device};
};

export default {
    getFwuTargetByIotTarget,
    getIotTargetByFwuTarget,
    getIotTargetNameByFwuTarget,
    isFwuTargetDevice,
    isFwuTargetHolderEngine,
    isFwuTargetHolderRelated,
    isFwuTargetRequiresUsbConnection,
    isFwuTargetBleStack,
    isFwuTargetRequiresSblTypeUpdate,
    getFwuTargets,
};
